import React, { Suspense, ComponentType, ReactElement } from 'react';
import { 
  Box, 
  CircularProgress, 
  Typography, 
  Skeleton,
  Card,
  CardContent 
} from '@mui/material';

// Enhanced loading fallback component with better UX
interface LoadingFallbackProps {
  message?: string;
  showSkeleton?: boolean;
  skeletonType?: 'page' | 'modal' | 'card' | 'table';
}

const LoadingFallback: React.FC<LoadingFallbackProps> = ({ 
  message = 'Loading...', 
  showSkeleton = false,
  skeletonType = 'page'
}) => {
  if (showSkeleton) {
    return <ComponentSkeleton type={skeletonType} />;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '200px',
        gap: 2,
        padding: 3
      }}
    >
      <CircularProgress size={40} thickness={4} />
      <Typography variant="body1" color="text.secondary">
        {message}
      </Typography>
    </Box>
  );
};

// Skeleton components for different UI patterns
const ComponentSkeleton: React.FC<{ type: 'page' | 'modal' | 'card' | 'table' }> = ({ type }) => {
  switch (type) {
    case 'page':
      return (
        <Box sx={{ p: 3 }}>
          <Skeleton variant="text" height={60} width="60%" sx={{ mb: 3 }} />
          <Skeleton variant="rectangular" height={200} sx={{ mb: 2 }} />
          <Skeleton variant="text" height={40} width="80%" sx={{ mb: 1 }} />
          <Skeleton variant="text" height={40} width="70%" sx={{ mb: 1 }} />
          <Skeleton variant="text" height={40} width="90%" />
        </Box>
      );
      
    case 'modal':
      return (
        <Box sx={{ p: 2 }}>
          <Skeleton variant="text" height={40} width="50%" sx={{ mb: 2 }} />
          <Skeleton variant="rectangular" height={120} sx={{ mb: 2 }} />
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <Skeleton variant="rectangular" height={36} width={80} />
            <Skeleton variant="rectangular" height={36} width={80} />
          </Box>
        </Box>
      );
      
    case 'card':
      return (
        <Card>
          <CardContent>
            <Skeleton variant="text" height={30} width="40%" sx={{ mb: 1 }} />
            <Skeleton variant="text" height={20} width="60%" sx={{ mb: 2 }} />
            <Skeleton variant="rectangular" height={100} />
          </CardContent>
        </Card>
      );
      
    case 'table':
      return (
        <Box>
          <Skeleton variant="text" height={40} width="30%" sx={{ mb: 2 }} />
          {[...Array(5)].map((_, index) => (
            <Box key={index} sx={{ display: 'flex', gap: 2, mb: 1 }}>
              <Skeleton variant="text" height={30} width="20%" />
              <Skeleton variant="text" height={30} width="30%" />
              <Skeleton variant="text" height={30} width="25%" />
              <Skeleton variant="text" height={30} width="25%" />
            </Box>
          ))}
        </Box>
      );
      
    default:
      return <LoadingFallback />;
  }
};

// HOC for lazy loading with enhanced error boundary
interface LazyWrapperProps {
  fallback?: ReactElement;
  errorFallback?: ReactElement;
  retryButton?: boolean;
}

export function withLazyLoading<T extends object>(
  Component: ComponentType<T>,
  options: LazyWrapperProps = {}
) {
  const {
    fallback = <LoadingFallback showSkeleton />,
    errorFallback,
    retryButton = true
  } = options;

  return React.forwardRef<any, T>((props, ref) => {
    const [hasError, setHasError] = React.useState(false);
    const [retryKey, setRetryKey] = React.useState(0);

    const handleRetry = () => {
      setHasError(false);
      setRetryKey(prev => prev + 1);
    };

    const defaultErrorFallback = (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '200px',
          gap: 2,
          padding: 3,
          textAlign: 'center'
        }}
      >
        <Typography variant="h6" color="error">
          Failed to load component
        </Typography>
        <Typography variant="body2" color="text.secondary">
          There was an error loading this component. Please try again.
        </Typography>
        {retryButton && (
          <button
            onClick={handleRetry}
            style={{
              padding: '8px 16px',
              backgroundColor: '#1976d2',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Retry
          </button>
        )}
      </Box>
    );

    if (hasError) {
      return errorFallback || defaultErrorFallback;
    }

    return (
      <Suspense fallback={fallback}>
        <ErrorBoundary onError={() => setHasError(true)}>
          <Component key={retryKey} {...props} ref={ref} />
        </ErrorBoundary>
      </Suspense>
    );
  });
}

// Simple error boundary for lazy components
class ErrorBoundary extends React.Component<
  { children: React.ReactNode; onError: () => void },
  { hasError: boolean }
> {
  constructor(props: { children: React.ReactNode; onError: () => void }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): { hasError: boolean } {
    return { hasError: true };
  }

  componentDidCatch() {
    this.props.onError();
  }

  render() {
    if (this.state.hasError) {
      return null; // Let parent handle error display
    }

    return this.props.children;
  }
}

// Preloader utility for critical components
export class ComponentPreloader {
  private static preloadedComponents = new Set<string>();

  static preload(componentLoader: () => Promise<any>, componentName: string): void {
    if (!this.preloadedComponents.has(componentName)) {
      this.preloadedComponents.add(componentName);
      
      // Preload on idle
      if ('requestIdleCallback' in window) {
        requestIdleCallback(() => {
          componentLoader().catch(() => {
            // Remove from preloaded set if failed
            this.preloadedComponents.delete(componentName);
          });
        });
      } else {
        // Fallback for browsers without requestIdleCallback
        setTimeout(() => {
          componentLoader().catch(() => {
            this.preloadedComponents.delete(componentName);
          });
        }, 100);
      }
    }
  }

  static isPreloaded(componentName: string): boolean {
    return this.preloadedComponents.has(componentName);
  }
}

// Hook for preloading components on user interaction
export function useComponentPreloader() {
  const preloadOnHover = React.useCallback((
    componentLoader: () => Promise<any>,
    componentName: string
  ) => {
    return {
      onMouseEnter: () => ComponentPreloader.preload(componentLoader, componentName),
      onFocus: () => ComponentPreloader.preload(componentLoader, componentName)
    };
  }, []);

  return { preloadOnHover };
}

export default LoadingFallback;